import React, { useMemo, useRef, useState, useCallback } from "react";
import { Modal } from "antd";
import {
  ContainerPanel,
  ContainerModal,
  CompsSearch,
  CompsTable,
  CompsForm,
  GenericCardsStatistic,
} from "@ms/shared-components";
import type { FormInstance } from "antd";
import SpaceDetail from "../../components/SpaceDetail";

// 导入样式
import "./index.css";

// 导入自定义hooks
import { useSpaceList } from "./hooks/useSpaceList";

// 导入配置
import {
  createSearchItems,
  createSearchButtons,
} from "./configs/panelSearchConfig";
import {
  createTableColumns,
  createTablePanelButtons,
  createTableTitle,
  TABLE_CONFIG,
  SEARCH_PANEL_CONFIG,
} from "./configs/panelTableConfig";
import { createStatisticData } from "./configs/panelStatisticConfig";
import {
  createSpaceFormItems,
  getDefaultFormValues,
  getFormTitle,
  FORM_WIDTH,
  FORM_HEIGHT,
  FORM_LAYOUT,
  createFormModalButtons,
} from "./configs/modalFormConfig";

const SpaceList: React.FC = () => {
  // 表单实例引用
  const formRef = useRef<FormInstance | null>(null);
  const searchFormRef = useRef<FormInstance | null>(null);
  const [tableColumnButton, setTableColumnButton] =
    useState<React.ReactNode | null>(null);

  // 使用自定义hook管理状态和逻辑
  const {
    spaces,
    statistics,
    loading,
    formModalVisible,
    detailModalVisible,
    editingSpace,
    selectedSpace,
    pagination,
    tags,
    loadSpaces,
    loadStatistics,
    handleSearch,
    handleCreate,
    handleAddChild,
    handleEdit,
    handleViewDetail,
    handleDelete,
    handleStatusToggle,
    handleFormSuccess,
    handlePaginationChange,
    closeFormModal,
    closeDetailModal,
  } = useSpaceList();

  // 使用 useMemo 优化搜索配置生成
  const searchItems = useMemo(() => {
    return createSearchItems(tags);
  }, [tags]);

  // 处理搜索按钮点击
  const handleSearchClick = useCallback(() => {
    if (searchFormRef.current) {
      searchFormRef.current
        .validateFields()
        .then((values) => {
          handleSearch(values);
        })
        .catch(() => {
          // 即使验证失败，也执行搜索（使用当前表单值）
          const currentValues = searchFormRef.current?.getFieldsValue() || {};
          handleSearch(currentValues);
        });
    }
  }, [handleSearch]);

  // 处理重置
  const handleReset = useCallback(() => {
    if (searchFormRef.current) {
      searchFormRef.current.resetFields();
    }
    const resetParams = {
      page: 1,
      pageSize: 10,
    };
    loadSpaces(resetParams);
    loadStatistics(resetParams); // 同时重置统计数据
  }, [loadSpaces, loadStatistics]);

  // 处理表单提交
  const handleFormSubmit = useCallback(async () => {
    if (!formRef.current) return;

    try {
      const values = await formRef.current.validateFields();
      const success = await handleFormSuccess(values);
      if (success) {
        formRef.current.resetFields();
      }
    } catch (error) {
      // 表单验证失败
    }
  }, [handleFormSuccess]);

  // 统计数据配置 - 使用接口返回的统计数据
  const statisticData = useMemo(
    () => createStatisticData(statistics),
    [statistics]
  );

  // 创建表格按钮配置
  const tableButtons = useMemo(
    () =>
      createTablePanelButtons({
        loadSpaces,
        handleCreate,
      }),
    [loadSpaces, handleCreate]
  );

  // 创建表格列配置
  const columns = useMemo(
    () =>
      createTableColumns({
        handleViewDetail,
        handleEdit,
        handleDelete,
        handleAddChild,
        handleStatusToggle,
      }),
    [
      handleViewDetail,
      handleEdit,
      handleDelete,
      handleAddChild,
      handleStatusToggle,
    ]
  );

  // 创建搜索按钮配置
  const searchButtons = useMemo(
    () =>
      createSearchButtons({
        handleSearchClick,
        handleReset,
      }),
    [handleSearchClick, handleReset]
  );

  // 表单弹窗按钮配置
  const formModalButtons = useMemo(
    () =>
      createFormModalButtons({
        closeFormModal,
        handleFormSubmit,
      }),
    [closeFormModal, handleFormSubmit]
  );

  // 表单默认值配置 - 使用 useMemo 避免重复计算
  const formInitialValues = useMemo(
    () => getDefaultFormValues(editingSpace),
    [editingSpace]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        height: "100%",
      }}
    >
      {/* 统计卡片 - 始终显示，避免页面闪烁 */}
      <GenericCardsStatistic data={statisticData} columns={4} />

      {/* 搜索面板 */}
      <ContainerPanel
        title={SEARCH_PANEL_CONFIG.SEARCH_TITLE}
        buttons={searchButtons}
      >
        <CompsSearch
          searchItems={searchItems}
          columns={SEARCH_PANEL_CONFIG.DEFAULT_COLUMNS}
          onSearch={handleSearch}
          onReset={handleReset}
          collapsible={SEARCH_PANEL_CONFIG.COLLAPSIBLE}
          defaultCollapsed={SEARCH_PANEL_CONFIG.DEFAULT_COLLAPSED}
          labelWidth={SEARCH_PANEL_CONFIG.LABEL_WIDTH}
          showButtons={false}
          formRef={(form) => {
            searchFormRef.current = form;
          }}
          key={tags.length} // 强制重新渲染当标签数据变化时
        />
      </ContainerPanel>

      {/* 表格面板 */}
      <ContainerPanel
        title={createTableTitle(tableColumnButton)}
        buttons={tableButtons}
        height="100%"
        cardProps={{
          style: { flex: 1, display: "flex", flexDirection: "column" },
        }}
      >
        <CompsTable
          tableProps={{
            columns,
            dataSource: spaces,
            rowKey: "id",
          }}
          loading={loading}
          showSelection={false}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number) => `共 ${total} 条记录`,
          }}
          onPaginationChange={handlePaginationChange}
          showColumnSetting={true}
          storageKey={TABLE_CONFIG.STORAGE_KEY}
          resizableColumns={true}
          saveColumnWidths={true}
          onColumnSettingButtonRender={(button) => {
            setTableColumnButton(button);
          }}
        />
      </ContainerPanel>

      {/* 表单弹窗 */}
      <ContainerModal
        title={getFormTitle(!!editingSpace, !!editingSpace?.parentId)}
        visible={formModalVisible}
        width={FORM_WIDTH}
        height={FORM_HEIGHT}
        footerRightButtons={formModalButtons}
        onCancel={closeFormModal}
        contentPadding={24}
      >
        <CompsForm
          formItems={createSpaceFormItems(editingSpace)}
          formRef={(form) => {
            formRef.current = form;
          }}
          initialValues={formInitialValues}
          layout={FORM_LAYOUT.layout}
          labelCol={FORM_LAYOUT.labelCol}
          wrapperCol={FORM_LAYOUT.wrapperCol}
          contentPadding={0}
        />
      </ContainerModal>

      {/* 详情弹窗 */}
      <Modal
        title={selectedSpace ? `空间详情 - ${selectedSpace.name}` : "空间详情"}
        open={detailModalVisible}
        onCancel={closeDetailModal}
        footer={null}
        width={800}
        destroyOnClose
        styles={{
          body: {
            padding: "24px 0",
            height: "calc(100% - 57px)",
            overflow: "auto",
          },
        }}
      >
        <SpaceDetail
          selectedSpace={selectedSpace}
          treeData={[]} // list页面没有树数据，传空数组
        />
      </Modal>
    </div>
  );
};

export default SpaceList;
